import { WaterfallHybridFooter } from "features/waterfall/hybrid/HybridFooter";
import { WaterfallEditMenu } from "features/waterfall/review/WaterfallEditMenu";
import {
  useFundFamilySubEventRetrieveSuspense,
  useRunWaterfall,
  useWaterfallInvestmentClassificationSummaryTableDataRetrieveSuspense,
} from "gen/endpoints/fund-family-sub-event/fund-family-sub-event";
import { useGlobalEntityRetrieveSuspense } from "gen/endpoints/global-entity/global-entity";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { DataTable } from "shared/components/table/data/DataTable";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";

export const HybridWaterfallInvestmentClassificationSummaryPage: React.FC =
  () => {
    const { t } = useTranslation(["common", "waterfall"]);

    const {
      fundFamilyEventId,
      fundFamilyId,
      fundFamilySubEventId,
      fundEntityId,
    } = useRequiredStringParams();

    const { data: fundFamilySubEventData } =
      useFundFamilySubEventRetrieveSuspense(
        fundFamilyId,
        fundFamilyEventId,
        fundFamilySubEventId,
      );

    const { data: tableData } =
      useWaterfallInvestmentClassificationSummaryTableDataRetrieveSuspense(
        fundFamilyId,
        fundFamilySubEventId,
        fundEntityId,
      );

    const { data: entityData } = useGlobalEntityRetrieveSuspense(
      fundFamilySubEventData.entity_id!,
    );

    const navigate = useNavigate();

    const activityRoute = useMemo(
      () =>
        rootRoute({})
          .fundFamilies({})
          .fundFamily({ fundFamilyId })
          .capitalActivity({})
          .capitalActivityEvent({ fundFamilyEventId }),
      [fundFamilyEventId, fundFamilyId],
    );

    const { mutateAsync: runWaterfall, isPending: waterfallIsLoading } =
      useRunWaterfall();

    const onContinue = async () => {
      await runWaterfall(
        {
          fundFamilyId,
          fundFamilySubEventId,
          fundEntityId,
        },
        {
          onSuccess: () => {
            navigate(
              activityRoute.waterfall({}).review({
                fundFamilySubEventId,
                fundEntityId,
              }).$,
            );
          },
        },
      );
    };
    return (
      <PageContent isFullHeight={true}>
        <PageHeader
          workflowName={t("waterfall:waterfallClassifications.hybridWaterfall")}
          title={t("waterfall:waterfallClassifications.summary.summary")}
          badge={{
            type: "default",
            text: entityData.legal_name,
          }}
          actions={
            <WaterfallEditMenu
              fundFamilyId={fundFamilyId}
              fundFamilyEventId={fundFamilyEventId}
              fundFamilySubEventId={fundFamilySubEventId}
              fundEntityId={fundEntityId}
              runWaterfall={false}
              isEqualizedView={undefined}
            />
          }
        />
        <PageBody>
          <TableBox isFullWidth={true}>
            <TableBoxBody>
              <DataTable data={tableData} />
            </TableBoxBody>
          </TableBox>
          <WaterfallHybridFooter
            onContinue={onContinue}
            isWaterfallLoading={waterfallIsLoading}
          />
        </PageBody>
      </PageContent>
    );
  };
